"use client"

import * as React from "react"
import { useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSort } from "@instadeepai/fontawesome-pro-regular"
import { faSortDown, faSortUp } from "@instadeepai/fontawesome-pro-solid"
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type PaginationState,
  type TableOptions,
  type TableState,
} from "@tanstack/react-table"

import { cn } from "../../lib/utils"
import {
  Table as BaseTable,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./base-table"
import PaginationControls from "./pagination-controls"

type TableProps<TData> = {
  data: TData[]
  columns: ColumnDef<TData>[]
  className?: string
  tableOptions?: TableOptions<TData>
  maxItemsPerPage?: number
  stateProps?: Partial<TableState> | undefined
  onRowClick?: (row: TData) => void
}

const Table = <TData,>({
  data = [],
  columns,
  className,
  tableOptions,
  maxItemsPerPage = 10,
  stateProps,
  onRowClick,
  ...props
}: TableProps<TData>) => {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: maxItemsPerPage,
  })

  const table = useReactTable({
    data,
    columns,
    rowCount: data.length,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    state: {
      pagination,
      ...stateProps,
    },
    ...tableOptions,
  })

  const rows = table.getRowModel().rows

  return (
    <>
      <div className="flex flex-col border border-gray-200">
        <BaseTable className={cn(className, "")} {...props}>
          <TableHeader className="bg-gray-50 text-sm text-gray-700">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder ? null : (
                      <div
                        className={
                          header.column.getCanSort()
                            ? "cursor-pointer select-none"
                            : ""
                        }
                        onClick={header.column.getToggleSortingHandler()}
                        title={
                          header.column.getCanSort()
                            ? header.column.getNextSortingOrder() === "asc"
                              ? "Sort ascending"
                              : header.column.getNextSortingOrder() === "desc"
                                ? "Sort descending"
                                : "Clear sort"
                            : undefined
                        }
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {{
                          asc: (
                            <span>
                              {" "}
                              <FontAwesomeIcon icon={faSortUp} />
                            </span>
                          ),
                          desc: (
                            <span>
                              {" "}
                              <FontAwesomeIcon icon={faSortDown} />
                            </span>
                          ),
                        }[header.column.getIsSorted() as string] ?? (
                          <span>
                            {" "}
                            <FontAwesomeIcon icon={faSort} />
                          </span>
                        )}
                      </div>
                    )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody className="rounded-b-lg">
            {rows?.length ? (
              rows.map((row) => (
                <TableRow
                  key={row.id}
                  className="h-16 cursor-pointer border-y text-sm text-gray-700 hover:bg-primary-25"
                  onClick={() => onRowClick?.(row.original)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No results found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </BaseTable>
      </div>

      <PaginationControls table={table} />
    </>
  )
}

Table.displayName = "Table"

export { Table }
